import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first, then system preference, then default to dark
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme && ['dark', 'light', 'light-dim']?.includes(savedTheme)) {
      return savedTheme;
    }
    
    // Check system preference
    if (typeof window !== 'undefined') {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)')?.matches;
      return systemPrefersDark ? 'dark' : 'light';
    }
    
    return 'dark';
  });

  const themes = [
    { 
      id: 'dark', 
      name: 'Dark', 
      description: 'Dark theme with futuristic accent',
      icon: 'Moon'
    },
    { 
      id: 'light', 
      name: 'Light', 
      description: 'Clean light theme',
      icon: 'Sun'
    },
    { 
      id: 'light-dim', 
      name: 'Light Dim', 
      description: 'Softer light theme',
      icon: 'Sunset'
    }
  ];

  const setThemeMode = (newTheme) => {
    if (!['dark', 'light', 'light-dim']?.includes(newTheme)) {
      console.warn(`Invalid theme: ${newTheme}. Using 'dark' as fallback.`);
      newTheme = 'dark';
    }
    
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const toggleTheme = () => {
    const currentIndex = themes?.findIndex(t => t?.id === theme);
    const nextIndex = (currentIndex + 1) % themes?.length;
    setThemeMode(themes?.[nextIndex]?.id);
  };

  const getNextTheme = () => {
    const currentIndex = themes?.findIndex(t => t?.id === theme);
    const nextIndex = (currentIndex + 1) % themes?.length;
    return themes?.[nextIndex];
  };

  const getCurrentTheme = () => {
    return themes?.find(t => t?.id === theme) || themes?.[0];
  };

  useEffect(() => {
    const root = document.documentElement;
    
    // Remove all theme classes
    root.classList?.remove('theme-dark', 'theme-light', 'theme-light-dim');
    
    // Apply the current theme class
    if (theme === 'light') {
      root.classList?.add('theme-light');
    } else if (theme === 'light-dim') {
      root.classList?.add('theme-light-dim');
    }
    // Dark theme is the default (no class needed)
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const themeColors = {
      dark: '#1C1A1A',
      light: '#FFFFFF',
      'light-dim': '#F8F9FA'
    };
    
    if (metaThemeColor) {
      metaThemeColor?.setAttribute('content', themeColors?.[theme] || themeColors?.dark);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'theme-color';
      meta.content = themeColors?.[theme] || themeColors?.dark;
      document.head?.appendChild(meta);
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e) => {
      // Only auto-switch if user hasn't manually set a theme
      const hasManualTheme = localStorage.getItem('theme');
      if (!hasManualTheme) {
        setTheme(e?.matches ? 'dark' : 'light');
      }
    };

    mediaQuery?.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery?.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  const value = {
    theme,
    setTheme: setThemeMode,
    toggleTheme,
    themes,
    getNextTheme,
    getCurrentTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    isLightDim: theme === 'light-dim'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;